<template>
  <addon-layout
    v-if="loading || error || needsGoogleAuth"
    title="Parcours TUT’TOP"
    :loading="loading"
    loading-label="Préparation de votre parcours…"
    :error="error"
  >
    <section
      v-if="needsGoogleAuth && !loading"
      class="auth-card"
    >
      <h2>Se connecter avec Google</h2>
      <p>Confirmez le compte étudiant actuellement ouvert dans Classroom.</p>
      <google-sign-in-button
        @click="connectGoogle"
      />
    </section>
  </addon-layout>
  <main
    v-else
    class="student-addon"
  >
    <div class="student-toolbar">
      <span>{{ workspace.name }}</span>
      <small>La note se synchronise automatiquement. Utilisez Classroom pour rendre le devoir.</small>
    </div>
    <journey-page
      :embedded-preview="true"
      :compact-mode="true"
      :preview-course-id="workspace.id"
      :preview-api-base="`/student/workspaces/${workspace.id}/journey`"
      :preview-access-token="appAccessToken"
      :external-student-id="studentId"
    />
  </main>
</template>

<script>
import JourneyPage from '@/views/student/dashboard/journey/JourneyPage.vue'
import {
  authorize,
  client,
  hasAddonSession,
  launchContext,
  storeAppAccessToken,
} from '@/services/googleClassroomAddon'
import AddonLayout from './AddonLayout.vue'
import GoogleSignInButton from './GoogleSignInButton.vue'

export default {
  name: 'GoogleClassroomAddonStudentView',
  components: { JourneyPage, AddonLayout, GoogleSignInButton },
  data() {
    return {
      loading: true,
      error: '',
      workspace: null,
      studentId: '',
      appAccessToken: '',
      needsGoogleAuth: !hasAddonSession(),
    }
  },
  async created() {
    if (hasAddonSession()) await this.initialize()
    else this.loading = false
  },
  mounted() {
    window.addEventListener('tuttop-google-addon-auth-expired', this.handleAuthExpired)
  },
  beforeDestroy() {
    window.removeEventListener('tuttop-google-addon-auth-expired', this.handleAuthExpired)
  },
  methods: {
    handleAuthExpired(event) {
      this.workspace = null
      this.appAccessToken = ''
      this.needsGoogleAuth = true
      const code = event && event.detail && event.detail.code
      this.error = code
        ? `Votre session de parcours a expiré. Reconnectez-vous pour continuer. (${code})`
        : 'Votre session de parcours a expiré. Reconnectez-vous pour continuer.'
    },
    async initialize() {
      this.loading = true
      this.error = ''
      try {
        const response = await client.post('/context', launchContext(this.$route))
        if (response.data.role !== 'student') throw new Error('Ce parcours doit être ouvert par un étudiant.')
        this.appAccessToken = String(response.data.appAccessToken || '')
        if (!this.appAccessToken) throw new Error('Le jeton du parcours étudiant est manquant.')
        storeAppAccessToken(this.appAccessToken)
        this.workspace = response.data.workspace
        this.studentId = String(response.data.studentId || '')
        this.needsGoogleAuth = false
      } catch (error) {
        this.error = error.response?.data?.error || error.message || 'Le parcours ne peut pas être ouvert.'
        if (error.response && error.response.status === 401) this.needsGoogleAuth = true
      } finally {
        this.loading = false
      }
    },
    async connectGoogle() {
      this.loading = true
      this.error = ''
      try {
        await authorize(this.$route)
        await this.initialize()
      } catch (error) {
        this.error = error.response?.data?.error || error.message
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.student-addon {
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f7f7fb;
}
.student-toolbar {
  flex: 0 0 auto;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 6px 14px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
  font-size: 0.92rem;
}
.student-toolbar small {
  color: #64748b;
  font-weight: 500;
  font-size: 0.78rem;
}
.student-addon ::v-deep .fh-page-compact {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.auth-card {
  margin: 24px;
  padding: 24px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: white;
  text-align: center;
}
</style>
